
import {Component, Prop} from 'vue-property-decorator';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import BaseFilterable from '@/components/reports-v3/components/filterables/BaseFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import gdbx from '@/store/modules/gdbx';
import moment from 'moment';
import {getTableName} from '@/worker/db/sqlHelpers';
import {FilterMatchMode} from 'primevue/api';
import {PermissionsGroup} from '@/store/models.def';
import userx from '@/store/modules/userx';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import {MomentInput} from 'moment/moment';

@Component({
    components: {
        TableFilterable,
        DateFilter,
        DataTable,
        Column,
        ColumnGroup,
        Row,
        Dropdown,
        InputText,
        MultiSelect,
        Button,
        DataInfo,
        BaseFilterable,
    },
})

export default class BudgetVarianceReport extends FilterWidget {

    public get permissionIds(): PermissionsGroup[] {
        return ['sales'];
    }

    public get exportFileNameOpex() {
        return 'OPEX Budget Variance Report from ' +
            moment(this.selectedDateRange[0]).format('DD MMM YY') +
            ' - ' +
            moment(this.selectedDateRange[1]).format('DD MMM YY') +
            ' selected month ' + moment(this.selectedCurrentMonth).format('MMM YY');
    }

    public get exportFileNameCapex() {
        return 'CAPEX Budget Variance Report from ' +
            moment(this.selectedDateRange[0]).format('DD MMM YY') +
            ' - ' +
            moment(this.selectedDateRange[1]).format('DD MMM YY') +
            ' selected month ' + moment(this.selectedCurrentMonth).format('MMM YY');
    }

    public get exportFileNameOpexSubtables() {
        return 'OPEX Subtables Report from ' +
            moment(this.selectedDateRange[0]).format('DD MMM YY') +
            ' - ' +
            moment(this.selectedDateRange[1]).format('DD MMM YY') +
            ' selected month ' + moment(this.selectedCurrentMonth).format('MMM YY');
    }

    public get exportFileNameCapexSubtables() {
        return 'CAPEX Subtables Report from ' +
            moment(this.selectedDateRange[0]).format('DD MMM YY') +
            ' - ' +
            moment(this.selectedDateRange[1]).format('DD MMM YY') +
            ' selected month ' + moment(this.selectedCurrentMonth).format('MMM YY');
    }

    public get dateFormatted() {
        return moment(this.selectedDateRange[0]).format('DD MMM YYYY')
        !== moment(this.selectedDateRange[1]).format('DD MMM YYYY')
            ? `${moment(this.selectedDateRange[0]).format('DD MMM YYYY')} - ${moment(
                this.selectedDateRange[1],
            ).format('DD MMM YYYY')}`
            : `As of ${moment(this.selectedDateRange[0]).format('DD MMM YYYY')}`;
    }

    public get dateFormatted2() {
        return `Budget from ${moment(this.selectedDateRange[0]).format('DD MMM YYYY')} - ${moment(this.selectedDateRange[1]).format('DD MMM YYYY')}`;
    }

    public get expensiveHook() {
        const {
            selectedAsOfDate,
            selectedAgents,
            selectedSuppliers,
            selectedDateRange,
            selectedCurrentMonth,
            selectedProjects,
        } = this;
        return JSON.stringify([
            selectedAsOfDate,
            selectedAgents,
            selectedSuppliers,
            selectedDateRange,
            selectedCurrentMonth,
            selectedProjects,
        ]);
    }

    public get user() {
        return userx.user!.email;
    }

    public get date() {
        return this.selectedDateRange;
    }

    public get dateRange() {
        return this.selectedDateRange;
    }

    public get currencySymbol() {
        return gdbx.currencySymbol + ' ';
    }

    // Computed property for unique Date values for filter dropdown
    public get uniqueDates(): Array<{ label: string; value: number }> {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.date).filter((v: any) => v))] as number[];
        const sorted = unique.sort((a, b) => a - b);
        return sorted.map((date) => ({
            label: moment(date).format('DD/MM/YYYY'),
            value: date,
        }));
    }

    // Computed property for unique Doc No values for filter dropdown
    public get uniqueDocNos(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.doc_no).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for unique Description values for filter dropdown
    public get uniqueDescriptions(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.description).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for unique GL Code values for filter dropdown
    public get uniqueGlCodes(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.gl_code).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for unique GL Account Name values for filter dropdown
    public get uniqueGlAccountNames(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.gl_account_name).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for unique Dtl Description values for filter dropdown
    public get uniqueDtlDescriptions(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.dtl_description).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for unique Project values for filter dropdown
    public get uniqueProjects(): string[] {
        if (!this.tableItems || this.tableItems.length === 0) {
            return [];
        }
        const unique = [...new Set(this.tableItems.map((item: any) => item.project).filter((v: any) => v))];
        return unique.sort() as string[];
    }

    // Computed property for OPEX subtables export data (filtered)
    public get opexSubtablesExport(): any[] {
        const result: any[] = [];
        const globalFilter = this.selectedFilter2.global;
        const filterValue = (globalFilter && globalFilter.value)
            ? globalFilter.value.toLowerCase() : '';

        for (const item of this.opexTableItems) {
            if (item.expanded && item.expanded.length > 0 && !item.isFooter) {
                const matchesFilter = !filterValue ||
                    (item.acc && item.acc.toLowerCase().includes(filterValue)) ||
                    (item.desc && item.desc.toLowerCase().includes(filterValue));

                if (matchesFilter) {
                    for (const expandedItem of item.expanded) {
                        const variancePercentage = expandedItem.ytdBudget && expandedItem.ytdBudget !== 0
                            ? Math.round((expandedItem.variance / expandedItem.ytdBudget) * 100)
                            : 0;
                        const yearToGoPercentage = expandedItem.budget && expandedItem.budget !== 0
                            ? Math.round((expandedItem.yearToGo / expandedItem.budget) * 100)
                            : 0;

                        result.push({
                            gl_account_code: item.acc,
                            gl_account_name: item.desc,
                            project: expandedItem.desc,
                            ytdBudget: expandedItem.ytdBudget,
                            ytdSpend: expandedItem.ytdSpend,
                            variance: expandedItem.variance,
                            variancePercentage,
                            budget: expandedItem.budget,
                            yearToGo: expandedItem.yearToGo,
                            yearToGoPercentage,
                        });
                    }
                }
            }
        }
        return result;
    }

// Computed property for CAPEX subtables export data (filtered)
    public get capexSubtablesExport(): any[] {
        const result: any[] = [];
        const globalFilter = this.selectedFilter4.global;
        const filterValue = (globalFilter && globalFilter.value)
            ? globalFilter.value.toLowerCase() : '';

        for (const item of this.capexTableItems) {
            if (item.expanded && item.expanded.length > 0 && !item.isFooter) {
                const matchesFilter = !filterValue ||
                    (item.acc && item.acc.toLowerCase().includes(filterValue)) ||
                    (item.desc && item.desc.toLowerCase().includes(filterValue));

                if (matchesFilter) {
                    for (const expandedItem of item.expanded) {
                        const variancePercentage = expandedItem.ytdBudget && expandedItem.ytdBudget !== 0
                            ? Math.round((expandedItem.variance / expandedItem.ytdBudget) * 100)
                            : 0;
                        const yearToGoPercentage = expandedItem.budget && expandedItem.budget !== 0
                            ? Math.round((expandedItem.yearToGo / expandedItem.budget) * 100)
                            : 0;

                        result.push({
                            gl_account_code: item.acc,
                            gl_account_name: item.desc,
                            project: expandedItem.desc,
                            ytdBudget: expandedItem.ytdBudget,
                            ytdSpend: expandedItem.ytdSpend,
                            variance: expandedItem.variance,
                            variancePercentage,
                            budget: expandedItem.budget,
                            yearToGo: expandedItem.yearToGo,
                            yearToGoPercentage,
                        });
                    }
                }
            }
        }
        return result;
    }

    @Prop() public selectedCurrentMonth !: string[];

    public filteredTableItemsTotal: any = 0;

    public loading: boolean = false;

    public loadingText: string = 'Loading Data.';

    public opexTableItems: any[] = [];
    public capexTableItems: any[] = [];

    public opexExpandedRows: any = [];
    public capexExpandedRows: any = [];

    public selectedFilter2: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    public selectedFilter3: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    public selectedFilter4: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    public selectedFilter5: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    public glTransFilter: any = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: null, matchMode: FilterMatchMode.IN },
        doc_no: { value: null, matchMode: FilterMatchMode.IN },
        description: { value: null, matchMode: FilterMatchMode.IN },
        gl_code: { value: null, matchMode: FilterMatchMode.IN },
        gl_account_name: { value: null, matchMode: FilterMatchMode.IN },
        dtl_description: { value: null, matchMode: FilterMatchMode.IN },
        project: { value: null, matchMode: FilterMatchMode.IN },
    };

    public tableItems: any = [];

    public tableFields: object[] = [];

    public tableItemsTotal: string = '';

    public filterIds: Array<
        | 'date'
        | 'month'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'project'
    > = ['suppliers', 'agents', 'dateAsOf', 'month', 'project'];

    public percentageFormatter(value: number | null): string {
        if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
            return '0%';
        }
        return Math.round(value) + '%';
    }

    public getVariancePercentage(data: any): number | null {
        if (!data.ytdBudget || data.ytdBudget === 0) {
            return null;
        }
        return (data.variance / data.ytdBudget) * 100;
    }

    public getYearToGoPercentage(data: any): number | null {
        if (!data.budget || data.budget === 0) {
            return null;
        }
        return (data.yearToGo / data.budget) * 100;
    }

    public getVariancePercentageColor(data: any): string {
        if (data.isHeader || data.isFooter) {
            return 'transparent';
        }
        const percentage = this.getVariancePercentage(data);
        if (percentage === null || percentage <= 0) {
            return '#ffcccc';
        }
        return 'transparent';
    }

    public getYearToGoPercentageColor(data: any): string {
        if (data.isHeader || data.isFooter) {
            return 'transparent';
        }
        const percentage = this.getYearToGoPercentage(data);
        if (percentage === null || percentage < 0 || percentage <= 20) {
            return '#ffcccc';
        } else if (percentage <= 50) {
            return '#ffe0b3';
        }
        return 'transparent';
    }

    public onGlTransFilter(event: any) {
        const filteredData = event.filteredValue || [];
        this.filteredTableItemsTotal = filteredData.reduce((a, b) => a + (b.amount || 0), 0);
    }

    public currencyTotal(amount) {
        if (!amount) {
            return null;
        }
        return `${this.currencySymbol} ${this.thousandFormatter(amount)}`;
    }

    public exportName(range: number[]) {
        return 'GL Trans Report from ' +
            moment(range[0] as MomentInput).format('DD MMM YY') +
            ' - ' +
            moment(range[1] as MomentInput).format('DD MMM YY');
    }

    // OPEX methods
    public toggleRowOpex(data: any) {
        const index = this.opexExpandedRows.findIndex((row: any) => row === data);
        if (index > -1) {
            this.opexExpandedRows.splice(index, 1);
        } else {
            this.opexExpandedRows.push(data);
        }
    }

    public isRowExpandedOpex(data: any) {
        return this.opexExpandedRows.includes(data);
    }

    public expandAllOpex() {
        this.opexExpandedRows = this.opexTableItems.filter((row) => row.expanded && row.expanded.length > 0);
    }

    public collapseAllOpex() {
        this.opexExpandedRows = [];
    }

    public exportCSVOpex() {
        if (this.$refs.dtOpex as any) {
            (this.$refs.dtOpex as any).exportCSV();
        }
    }

    public exportCSVOpexExpand() {
        if (this.$refs.dtOpexExpand as any) {
            (this.$refs.dtOpexExpand as any).exportCSV();
        }
    }

    public exportCSVOpexSubtables() {
        if (this.$refs.dtOpexSubtables as any) {
            (this.$refs.dtOpexSubtables as any).exportCSV();
        }
    }

    // CAPEX methods
    public toggleRowCapex(data: any) {
        const index = this.capexExpandedRows.findIndex((row: any) => row === data);
        if (index > -1) {
            this.capexExpandedRows.splice(index, 1);
        } else {
            this.capexExpandedRows.push(data);
        }
    }

    public isRowExpandedCapex(data: any) {
        return this.capexExpandedRows.includes(data);
    }

    public expandAllCapex() {
        this.capexExpandedRows = this.capexTableItems.filter((row) => row.expanded && row.expanded.length > 0);
    }

    public collapseAllCapex() {
        this.capexExpandedRows = [];
    }

    public exportCSVCapex() {
        if (this.$refs.dtCapex as any) {
            (this.$refs.dtCapex as any).exportCSV();
        }
    }

    public exportCSVCapexExpand() {
        if (this.$refs.dtCapexExpand as any) {
            (this.$refs.dtCapexExpand as any).exportCSV();
        }
    }

    public exportCSVCapexSubtables() {
        if (this.$refs.dtCapexSubtables as any) {
            (this.$refs.dtCapexSubtables as any).exportCSV();
        }
    }

    public exportCSVGlTrans() {
        if (this.$refs.dtGlTrans as any) {
            (this.$refs.dtGlTrans as any).exportCSV();
        }
    }

    public formatDate(value: any) {
        if (!value) { return ''; }
        return moment(value).format('DD/MM/YYYY');
    }

    public glTransExportFunction(data: { data: any; field: string }) {
        if (data.field === 'date' && data.data) {
            return moment(data.data).format('DD/MM/YYYY');
        }
        if (data.field === 'amount' && data.data !== null && data.data !== undefined) {
            return Number(data.data).toFixed(2);
        }
        return data.data;
    }

    public budgetExportFunction(data: { data: any; field: string }) {
        const currencyFields = ['ytdBudget', 'ytdSpend', 'variance', 'budget', 'yearToGo'];
        if (currencyFields.includes(data.field) && data.data !== null && data.data !== undefined) {
            return Number(data.data).toFixed(2);
        }
        return data.data;
    }

    public exportFileName2(x) {
        return x + ' ' +
            moment(this.selectedDateRange[0]).format('DD MMM YY') +
            ' - ' +
            moment(this.selectedDateRange[1]).format('DD MMM YY') +
            ' selected month ' + moment(this.selectedCurrentMonth).format('MMM YY');
    }

    public thousandFormatter(x) {
        return Number(x).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    public getYearToGoColor(data: any): string {
        if (data.isHeader || data.isFooter) {
            return 'transparent';
        }

        if (data.yearToGo <= 0) {
            return '#ffcccc';
        }

        if (!data.budget || data.budget === 0) {
            return 'transparent';
        }

        const percentageLeft = (data.yearToGo / data.budget) * 100;

        if (percentageLeft <= 20) {
            return '#ffcccc';
        } else if (percentageLeft <= 50) {
            return '#ffe0b3';
        }
        return 'transparent';
    }

    public getPercentage = (a, b) => {
        const percentage = Math.round(a / b * 100);

        if (percentage < 0 || percentage == null) {
            return -100;
        }

        return percentage;
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        this.collapseAllOpex();
        this.collapseAllCapex();

        // Load all tables at once
        const queryHead = await this._ensureTablesLoaded([
            'diyUserAccess', 'globalProjects', 'diyBudgetVsActual', 'diyGLTrans',
        ] as any[]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.5);

        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const userAccessTable = getTableName('diyUserAccess');
        const projectsTable = getTableName('globalProjects');
        const budgetTable = getTableName('diyBudgetVsActual');
        const glTransTable = getTableName('diyGLTrans');

        // Phase 1: Get user-accessible projects via rawQuery
        const accessRows = await queryHead.rawQuery(
            `SELECT "PROJECT" as project FROM ${userAccessTable} WHERE "COMPANYNAME" = '${escapeStr(this.user)}'`,
        );
        this.checkInterrupted(jobId);

        const accessableProjects: string[] = accessRows.map((r: any) => String(r.project));

        const selectedSet = new Set(this.selectedProjects);
        const consolidatedProjects = accessableProjects.filter((p) => selectedSet.has(p));

        if (consolidatedProjects.length === 0) {
            this.opexTableItems = [];
            this.capexTableItems = [];
            this.tableItems = [];
            this.tableFields = [];
            return;
        }

        const projectList = consolidatedProjects.map((p) => `'${escapeStr(p)}'`).join(',');
        const projectFilter = `"PROJECT" IN (${projectList})`;

        // Phase 2: Get project descriptions via rawQuery
        const projectRows = await queryHead.rawQuery(
            `SELECT "CODE" as code, "DESCRIPTION" as project_desc FROM ${projectsTable} WHERE "CODE" IN (${projectList})`,
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.5, 0.9);

        const projectDescMap = new Map<string, string>();
        for (const row of projectRows) {
            projectDescMap.set(String(row.code), String(row.project_desc));
        }

        // Phase 3: Budget + GL Trans in parallel via rawQuery
        const [startDate, endDate] = this.selectedDateRange;
        const startYM = moment(startDate).year() * 100 + (moment(startDate).month() + 1);
        const endYM = moment(endDate).year() * 100 + (moment(endDate).month() + 1);

        const ytdCutoffMonth = moment(this.selectedCurrentMonth).month();
        const ytdCutoffYear = moment(this.selectedCurrentMonth).year();
        const ytdYM = ytdCutoffYear * 100 + (ytdCutoffMonth + 1);

        const ytdCondition = `(TRY_CAST("BYEAR" AS INTEGER) * 100 + TRY_CAST("BMONTH" AS INTEGER)) <= ${ytdYM}`;
        const dateRangeCondition = `(TRY_CAST("BYEAR" AS INTEGER) * 100 + TRY_CAST("BMONTH" AS INTEGER)) BETWEEN ${startYM} AND ${endYM}`;
        const glTransDateFilter = `TRY_CAST("POSTDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

        const [budgetRows, glTransRows] = await Promise.all([
            queryHead.rawQuery(`
                SELECT
                    "ACCGROUP" as acc_group,
                    "ACCOUNT" as acc,
                    "DESCRIPTION" as "desc",
                    "PROJECT" as project,
                    SUM(CASE WHEN ${ytdCondition}
                        THEN TRY_CAST("BUDGET" AS DOUBLE) ELSE 0 END) as ytd_budget,
                    SUM(CASE WHEN ${ytdCondition}
                        THEN TRY_CAST("YTDSPEND" AS DOUBLE) ELSE 0 END) as ytd_spend,
                    SUM(CASE WHEN ${ytdCondition}
                        THEN TRY_CAST("VARIANCE" AS DOUBLE) ELSE 0 END) as ytd_variance,
                    SUM(TRY_CAST("BUDGET" AS DOUBLE)) as full_year_budget
                FROM ${budgetTable}
                WHERE ${projectFilter} AND ${dateRangeCondition}
                GROUP BY "ACCGROUP", "ACCOUNT", "DESCRIPTION", "PROJECT"
                ORDER BY "ACCGROUP", "DESCRIPTION", "PROJECT"
            `),
            queryHead.rawQuery(`
                SELECT
                    TRY_CAST("POSTDATE" AS BIGINT) as date,
                    "DOCNO" as doc_no,
                    "DESCRIPTION" as descr,
                    "GLACCCODE" as gl_code,
                    "GLACCNAME" as gl_account_name,
                    "DTLDESC" as dtl_description,
                    "PROJECT" as project,
                    TRY_CAST("AMOUNT" AS DOUBLE) as amount
                FROM ${glTransTable}
                WHERE ${projectFilter} AND ${glTransDateFilter}
                ORDER BY TRY_CAST("POSTDATE" AS BIGINT)
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.95);

        // Phase 4: Assemble budget variance nested structure from flat SQL results
        const opexArray: any[] = [];
        const capexArray: any[] = [];

        // Group budget rows: accGroup → desc → [project rows]
        const accGroupMap = new Map<string, Map<string, { acc: string; projRows: any[] }>>();

        for (const row of budgetRows) {
            const accGroup = String(row.acc_group);
            const desc = String(row.desc);
            const acc = String(row.acc);

            if (!accGroupMap.has(accGroup)) {
                accGroupMap.set(accGroup, new Map());
            }
            const descMap = accGroupMap.get(accGroup)!;
            if (!descMap.has(desc)) {
                descMap.set(desc, { acc, projRows: [] });
            }
            descMap.get(desc)!.projRows.push(row);
        }

        for (const [accGroupKey, descMap] of accGroupMap) {
            const targetArray = accGroupKey === 'OPEX' ? opexArray : capexArray;

            let groupTotalYtdSpend = 0;
            let groupTotalYtdBudget = 0;
            let groupTotalVariance = 0;
            let groupTotalBudget = 0;
            let groupTotalYearToGo = 0;

            for (const [descKey, { acc, projRows }] of descMap) {
                let totalYtdBudget = 0;
                let totalYtdSpend = 0;
                let totalYtdVariance = 0;
                let totalFullYearBudget = 0;

                const expandedItems: any[] = [];

                for (const row of projRows) {
                    const pYtdBudget = Number(row.ytd_budget) || 0;
                    const pYtdSpend = Number(row.ytd_spend) || 0;
                    const pYtdVariance = Number(row.ytd_variance) || 0;
                    const pFullYearBudget = Number(row.full_year_budget) || 0;
                    const pYearToGo = pFullYearBudget - pYtdSpend;
                    const projectKey = String(row.project);

                    totalYtdBudget += pYtdBudget;
                    totalYtdSpend += pYtdSpend;
                    totalYtdVariance += pYtdVariance;
                    totalFullYearBudget += pFullYearBudget;

                    expandedItems.push({
                        timestamp: null,
                        project: '',
                        acc,
                        code: projectKey,
                        desc: projectDescMap.get(projectKey) || projectKey,
                        accGroup: accGroupKey,
                        budget: pFullYearBudget,
                        ytdSpend: pYtdSpend,
                        variance: pYtdVariance,
                        variancePercent: this.getPercentage(pYtdVariance, pYtdBudget),
                        ytdBudget: pYtdBudget,
                        yearToGo: pYearToGo,
                        yearToGoPercent: this.getPercentage(pYearToGo, pFullYearBudget),
                    });
                }

                const yearToGo = totalFullYearBudget - totalYtdSpend;

                groupTotalYtdSpend += totalYtdSpend;
                groupTotalYtdBudget += totalYtdBudget;
                groupTotalVariance += totalYtdVariance;
                groupTotalBudget += totalFullYearBudget;
                groupTotalYearToGo += yearToGo;

                targetArray.push({
                    timestamp: null,
                    project: '',
                    acc,
                    desc: descKey,
                    accGroup: accGroupKey,
                    budget: totalFullYearBudget,
                    ytdSpend: totalYtdSpend,
                    variance: totalYtdVariance,
                    variancePercent: this.getPercentage(totalYtdVariance, totalYtdBudget),
                    ytdBudget: totalYtdBudget,
                    yearToGo,
                    yearToGoPercent: this.getPercentage(yearToGo, totalFullYearBudget),
                    expanded: expandedItems,
                });
            }

            targetArray.push({
                timestamp: null,
                project: '',
                acc: `Total ${accGroupKey}`,
                desc: '',
                accGroup: accGroupKey,
                budget: groupTotalBudget,
                ytdSpend: groupTotalYtdSpend,
                variance: groupTotalVariance,
                variancePercent: this.getPercentage(groupTotalVariance, groupTotalYtdBudget),
                ytdBudget: groupTotalYtdBudget,
                yearToGo: groupTotalYearToGo,
                yearToGoPercent: this.getPercentage(groupTotalYearToGo, groupTotalBudget),
                isFooter: true,
            });
        }

        // Phase 5: Process GL Trans flat results
        const glTransTableData: any[] = (glTransRows as any[]).map((row: any) => ({
            date: Number(row.date) || 0,
            doc_no: String(row.doc_no || ''),
            description: String(row.descr || ''),
            gl_code: String(row.gl_code || ''),
            gl_account_name: String(row.gl_account_name || ''),
            dtl_description: String(row.dtl_description || ''),
            project: String(row.project || ''),
            amount: Number(row.amount) || 0,
        }));

        this.tableFields = [
            {
                key: 'date',
                sortable: false,
                formatter: TableItemFormatter.dateWithSlashes,
                exportFormatter: (value: any) => value ? moment(value).format('DD/MM/YYYY') : '',
            },
            {
                key: 'doc_no',
                sortable: false,
            },
            {
                key: 'description',
                sortable: false,
            },
            {
                key: 'gl_code',
                sortable: false,
            },
            {
                key: 'gl_account_name',
                sortable: false,
            },
            {
                key: 'dtl_description',
                sortable: false,
            },
            {
                key: 'project',
                sortable: false,
            },
            {
                key: 'amount',
                sortable: false,
                formatter: TableItemFormatter.basicWithDecimals,
                title: 'Amount (MYR)',
            },
        ];

        this.tableItems = glTransTableData;
        this.tableItemsTotal = glTransTableData.reduce((a: number, b: any) => a + b.amount, 0) as any;
        this.filteredTableItemsTotal = this.tableItemsTotal;

        this.opexTableItems = opexArray;
        this.capexTableItems = capexArray;

        this.generateLoadingText(1);
        this.saveHistory('opexTableItems', 'capexTableItems', 'tableItems', 'tableFields', 'tableItemsTotal', 'filteredTableItemsTotal');
    }
}
